import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { ClipboardList, Upload, Download, AlertCircle, Trash2, FileText } from 'lucide-react';
import { formatDateTime, getEventTypeLabel } from '../../lib/utils';

const AuditLogPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await adminAPI.getAuditLog(200);
        setEvents(response.data);
      } catch (error) {
        toast.error('Erreur lors du chargement du journal');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getEventIcon = (type) => {
    switch (type) {
      case 'televersement':
        return <Upload className="w-4 h-4 text-green-600" />;
      case 'telechargement':
        return <Download className="w-4 h-4 text-blue-600" />;
      case 'changement_statut':
        return <AlertCircle className="w-4 h-4 text-amber-600" />;
      case 'suppression':
        return <Trash2 className="w-4 h-4 text-red-600" />;
      default:
        return <FileText className="w-4 h-4 text-slate-600" />;
    }
  };

  const getEventBadgeColor = (type) => {
    switch (type) {
      case 'televersement':
        return 'bg-green-100 text-green-800';
      case 'telechargement':
        return 'bg-blue-100 text-blue-800';
      case 'changement_statut':
        return 'bg-amber-100 text-amber-800';
      case 'suppression':
        return 'bg-red-100 text-red-800';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Journal d'audit</h1>
        <p className="text-slate-600">Historique de toutes les actions sur les fichiers.</p>
      </div>

      <Card>
        <CardContent className="p-0">
          {events.length === 0 ? (
            <div className="py-12 text-center">
              <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Aucune activité enregistrée.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {events.map((event) => (
                <div key={event.id} className="flex items-start gap-4 p-4 hover:bg-slate-50 transition-colors">
                  <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                    {getEventIcon(event.type_evenement)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{event.acteur_nom || 'Utilisateur inconnu'}</span>
                      <Badge className={`${getEventBadgeColor(event.type_evenement)}`} variant="secondary">
                        {getEventTypeLabel(event.type_evenement)}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {event.fichier_nom && (
                        <>
                          Fichier: <span className="font-medium">{event.fichier_nom}</span>
                          {event.projet_nom && <> • Projet: {event.projet_nom}</>}
                        </>
                      )}
                    </p>
                    {event.type_evenement === 'changement_statut' && event.metadata && (
                      <p className="text-sm text-slate-500 mt-1">
                        {event.metadata.ancien_statut} → {event.metadata.nouveau_statut}
                      </p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      {formatDateTime(event.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogPage;
